<template>
    <div :class="cssClass" v-on:click.capture="editMode" v-click-outside="viewMode">

        <draggable
            :list="widgets"
            class="list-group"
            group="widgets"
            ghost-class="ghost"
            :disabled="(mode != 'edit' && !dragging) || hasWidgetInEditMode"
            :move="checkMove"
            @start="startDragging"
            @end="stopDragging"
        >
            <widget
                :is="widget.type"
                v-for="(widget, index) in widgets"
                :key="index"
                :id="widget.id"
                :read-only="readOnly"
                ref="widgets"
            ></widget>
        </draggable>

        <div v-if="mode == 'edit'">
            <span :class="{'cms-option-button large':true, 'active' : viewMenu == 'add'}" @click="toggleAddMenu">
                <span class="icon_plus"></span>
                {{ $t('new_element') }}
            </span>

            <span :class="{'cms-option-button large':true, 'active' : viewMenu == 'options'}" @click="toggleOptionsMenu">
                <span class="icon_menu"></span>
                {{ $t('options') }}
            </span>

            <span class="cms-option-button large move-handle">
                <span class="arrow_move"></span>
                {{ $t('move') }}
            </span>
        </div>

        <fieldset v-if="isAddMenuOpen" class="cms-buttons-container">
            <legend>{{ $t('new_element') }}:</legend>
            <span class="cms-option-button" v-on:click="addWidget('paragraph-widget')">
                <span class="fa fa-file-text-o"></span>
                {{ $t('paragraph') }}
            </span>
            <span class="cms-option-button" v-on:click="addWidget('image-widget')">
                <span class="fa fa-image"></span>
                {{ $t('image') }}
            </span>
            <span class="cms-option-button" v-on:click="addWidget('header-widget')">
                <span class="fa fa-header"></span>
                {{ $t('header') }}
            </span>
            <span class="cms-option-button" v-on:click="addWidget('file-widget')">
                <span class="fa fa-file-pdf-o"></span>
                {{ $t('file') }}
            </span>
            <span class="cms-option-button" v-on:click="addWidget('html-widget')">
                <span class="fa fa-code"></span>
                {{ $t('html') }}
            </span>
        </fieldset>

        <block-options
            v-if="isOptionsMenuOpen"
            :width="block.width"
            :title="$t('options') + ':'"
            :individual="true"
            @set-width="setWidth"
            @destroy="destroy"
            @copy="copy"
        ></block-options>

        <span class="icon icon-close" v-on:click.capture="viewMode" v-if="mode == 'edit'" tooltip="Stop editing">
            <i class="fa fa-close"></i>
        </span>
    </div>
</template>

<script>
    import draggable from "vuedraggable";
    import {mapState} from 'vuex';

    export default {
        components: {
            draggable
        },
        props: {
            id: {
                type: Number,
            },
            readOnly: {
                type: Boolean,
                default: true,
            },
        },
        data: function () {
            return {
                mode: 'view',
                viewMenu: 'add',
                swap: {},
            }
        },
        computed: {
              ...mapState(['dragging']),
            block: function () {
                return this.$store.getters.getBlock(this.id);
            },
            isAddMenuOpen: function () {
                return !this.readOnly && this.mode == 'edit' && this.viewMenu == 'add'
            },
            isOptionsMenuOpen: function () {
                return !this.readOnly && this.mode == 'edit' && this.viewMenu == 'options'
            },
            widgets: function () {
                return this.block.widgets || [];
            },
            hasWidgetInEditMode() {
                if (!this.$refs.widgets) {
                    return false;
                }

                return this.$refs.widgets.filter(widget => widget.mode && widget.mode == 'edit').length > 0;
            },
            cssClass: function () {
                var result = 'default-block ' + this.columnWidth;

                if (!this.readOnly && this.mode == 'view') {
                    result += ' editable';
                }

                return result;
            },

            columnWidth: function () {
                var results = 'col-md-' + this.block.width;
                results += ' col-sm-' + this.block.width;

                if (this.block.width > 3) {
                    results += ' col-xs-12';
                } else {
                    results += ' col-xs-6';
                }

                return results;
            },

            nextWidgetPosition: function () {
                if (!this.widgets.length) {
                    return 1;
                }

                return Math.max.apply(0, this.widgets.map(i => i.position)) + 1;
            },
        },
        methods: {
            checkMove: function(event) {
                this.swap.old = event.relatedContext.element;
                this.swap.new = event.draggedContext.element;
                this.swap.after = event.willInsertAfter + 0; // cast to int

                /**
                 * In case the widget is moved to an empty block, swapping
                 * isn't possible. In this case, just change the block_id of
                 * the dragged widget.
                 */
                if (!event.relatedContext.element) {
                    this.swap.newBlockId = event.relatedContext.component.$parent.id
                }
            },
            startDragging: function () {
                this.$store.commit('START_DRAGGING');
            },
            stopDragging: function () {
                this.$store.commit('STOP_DRAGGING');

                // If the swap object is empty, nothing has been moved;
                if (!this.swap.old || !this.swap.new) {
                    return;
                }

                if (!this.swap.old) {
                    this.swap.new.block_id = this.swap.newBlockId;
                    this.swap.new.position = 0;

                    this.$store.dispatch('saveWidget', this.swap.new);
                } else {
                    this.$store.dispatch('repositionWidget', this.swap)
                        .then(() => {this.swap = {}})
                }
            },
            toggleAddMenu: function () {
                this.viewMenu = (this.viewMenu == 'add') ? null : 'add';;
            },
            toggleOptionsMenu: function () {
                this.viewMenu = (this.viewMenu == 'options') ? null : 'options';
            },
            editMode: function (event) {
                if (this.readOnly || this.mode == 'edit') {
                    return true;
                }

                if (event) {
                    event.stopPropagation();
                    event.preventDefault();
                }
                this.mode = 'edit';
            },

            viewMode: function () {
                this.mode = 'view';
                this.viewMenu = null;

                this.$refs.widgets && this.$refs.widgets.map(i => i.cancel());

                if (!this.$refs.widgets || !this.$refs.widgets.length) {
                    this.destroy();
                }
            },

            setWidth(width) {
                this.viewMenu = null;
                this.block.width = width;
                this.save();
            },

            destroy: function () {
                if (!(this.$refs.widgets && this.$refs.widgets.length) || confirm(this.$i18n.t('confirm_delete_block'))) {
                    this.$store.dispatch('deleteBlock', this.block);
                }
            },

            copy: function (locale) {
                this.$store.dispatch('copyBlock', {
                    block: this.block,
                    locale: locale,
                }).then(response => {
                    alert(this.$i18n.t('copy_success'));
                    this.viewMode();
                });
            },

            addWidget: function (type) {
                this.viewMenu = null;

                let options = {
                    id: Math.random(), // If the widget's id is a float, it's unsaved
                    contents: '',
                    block_id: this.id,
                    type: type,
                    position: this.nextWidgetPosition,
                }

                this.$store.commit('ADD_WIDGET', options);
            },

            save: function () {
                this.$store.dispatch('saveBlock', this.block);
            },
        },
        mounted: function () {
            if (this.block.isNewRecord) {
                this.editMode();
                delete this.block.isNewRecord;
            }
        },
    }
</script>



// WEBPACK FOOTER //
// resources/js/components/blocks/DefaultBlock.vue